import { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './WhatsappTemplates.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];

const blank = () => ({
  template_key:  '',
  template_name: '',
  language_code: 'en',
  category:      'UTILITY',
  param_labels:  '',
  description:   '',
  is_active:     true,
});

/* ── Modal ──────────────────────────────────────────────────── */
const TemplateModal = ({ initial, token, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial
      ? {
          template_key:  initial.template_key  || '',
          template_name: initial.template_name || '',
          language_code: initial.language_code || 'en',
          category:      initial.category      || 'UTILITY',
          param_labels:  initial.param_labels  ? JSON.stringify(initial.param_labels, null, 2) : '',
          description:   initial.description   || '',
          is_active:     initial.is_active !== false,
        }
      : blank()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.param_labels.trim()) {
      try { JSON.parse(form.param_labels); }
      catch { setError('Param Labels is not valid JSON.'); return; }
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        template_key: form.template_key.trim().toUpperCase(),
        param_labels: form.param_labels.trim() || null,
      };
      if (isEdit) {
        await axios.put(`${API_URL}/whatsapp-templates/${initial.id}`, payload, auth(token));
      } else {
        await axios.post(`${API_URL}/whatsapp-templates`, payload, auth(token));
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wt-overlay" onClick={onClose}>
      <div className="wt-modal" onClick={e => e.stopPropagation()}>
        <div className="wt-modal-header">
          <h2>{isEdit ? `Edit — ${initial.template_key}` : 'Add Template'}</h2>
          <button className="wt-modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="wt-modal-body">
          {error && <div className="alert alert-error"><span className="alert-icon">⚠</span>{error}</div>}

          <div className="wt-form-row">
            <div className="wt-form-group">
              <label>Template Key *</label>
              <input
                className="wt-input wt-mono"
                value={form.template_key}
                onChange={e => set('template_key', e.target.value.toUpperCase())}
                placeholder="e.g. ORDER_PLACED_CUSTOMER"
                required
              />
              <span className="wt-hint">Stable code used in application — never changes</span>
            </div>
            <div className="wt-form-group">
              <label>Template Name *</label>
              <input
                className="wt-input wt-mono"
                value={form.template_name}
                onChange={e => set('template_name', e.target.value)}
                placeholder="e.g. lh_online_order_placed_v2"
                required
              />
              <span className="wt-hint">Actual name in Meta Business Manager</span>
            </div>
          </div>

          <div className="wt-form-row">
            <div className="wt-form-group wt-form-group--sm">
              <label>Language Code</label>
              <input
                className="wt-input"
                value={form.language_code}
                onChange={e => set('language_code', e.target.value)}
                placeholder="en"
                maxLength={10}
              />
            </div>
            <div className="wt-form-group wt-form-group--sm">
              <label>Category</label>
              <select className="wt-input" value={form.category} onChange={e => set('category', e.target.value)}>
                <option value="">— none —</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="wt-form-group wt-form-group--sm wt-active-group">
              <label>Active</label>
              <input
                type="checkbox"
                className="wt-checkbox"
                checked={form.is_active}
                onChange={e => set('is_active', e.target.checked)}
              />
            </div>
          </div>

          <div className="wt-form-group wt-form-group--full">
            <label>Param Labels (JSON)</label>
            <textarea
              className="wt-input wt-textarea wt-mono"
              value={form.param_labels}
              onChange={e => set('param_labels', e.target.value)}
              placeholder={'{"1":"customer_name","2":"order_no"}'}
              rows={3}
            />
            <span className="wt-hint">Maps each &#123;&#123;n&#125;&#125; placeholder to its purpose. Documentation only.</span>
          </div>

          <div className="wt-form-group wt-form-group--full">
            <label>Description</label>
            <textarea
              className="wt-input wt-textarea"
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="When this template is sent…"
              rows={2}
            />
          </div>

          <div className="wt-modal-footer">
            <button type="button" className="wt-btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="wt-btn-save" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Add Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Main Page ──────────────────────────────────────────────── */
const WhatsappTemplates = () => {
  const { user } = useContext(AuthContext);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API_URL}/whatsapp-templates`, auth(user?.token));
      setRows(r.data);
    } catch {
      setError('Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleSave = () => {
    setModal(null);
    setSuccess('Template saved.');
    setTimeout(() => setSuccess(''), 4000);
    load();
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete template "${row.template_key}"?\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/whatsapp-templates/${row.id}`, auth(user?.token));
      setSuccess(`Template "${row.template_key}" deleted.`);
      setTimeout(() => setSuccess(''), 4000);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed.');
    }
  };

  const catBadge = (cat) => {
    if (!cat) return null;
    const cls = { UTILITY: 'wt-badge--utility', MARKETING: 'wt-badge--marketing', AUTHENTICATION: 'wt-badge--auth' }[cat] || '';
    return <span className={`wt-badge ${cls}`}>{cat}</span>;
  };

  return (
    <div className="users-management">
      <div className="users-header">
        <div className="header-content">
          <h1>WhatsApp Templates</h1>
          <p>Map stable application keys to Meta Business Manager template names</p>
        </div>
        <button className="btn-add-user" onClick={() => setModal({ type: 'add' })}>
          <span className="icon">+</span> Add Template
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>{error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>{success}
          <button className="alert-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      <div className="wt-count">{rows.length} template{rows.length !== 1 ? 's' : ''}</div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Template Key</th>
              <th>Template Name</th>
              <th>Lang</th>
              <th>Category</th>
              <th>Param Labels</th>
              <th>Description</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="wt-loading">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="wt-empty">No templates yet.</td></tr>
            ) : rows.map(row => (
              <tr key={row.id} className={row.is_active ? '' : 'wt-row--inactive'}>
                <td><code className="wt-key">{row.template_key}</code></td>
                <td><code className="wt-name">{row.template_name}</code></td>
                <td>{row.language_code}</td>
                <td>{catBadge(row.category)}</td>
                <td>
                  {row.param_labels
                    ? <code className="wt-params">{JSON.stringify(row.param_labels)}</code>
                    : <span className="wt-none">—</span>}
                </td>
                <td className="wt-desc">{row.description || <span className="wt-none">—</span>}</td>
                <td>{row.is_active ? '✓' : <span className="wt-none">✗</span>}</td>
                <td className="wt-actions">
                  <button className="wt-btn-edit" onClick={() => setModal({ type: 'edit', row })}>Edit</button>
                  <button className="wt-btn-del"  onClick={() => handleDelete(row)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <TemplateModal
          initial={modal.type === 'edit' ? modal.row : null}
          token={user?.token}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default WhatsappTemplates;
